// Live notifications feed for the signed-in user.
//
// Backed by existing tables — no new schema required:
//   - tryons     → "Your try-on / aura is ready" (status flips to complete)
//                  or "We refunded your credit" (failed + refunded)
//   - credit_transactions → "You earned N credits" / purchase confirmations
//
// Unread tracking is local-only: we persist the most-recent timestamp the
// user has acknowledged in localStorage and count anything newer as unread.
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type NotificationKind =
  | "tryon_ready"
  | "aura_ready"
  | "tryon_failed"
  | "credits_granted"
  | "credits_purchased"
  | "broadcast"
  | "featured_approved"
  | "featured_rejected";

export interface AppNotification {
  id: string;
  kind: NotificationKind;
  title: string;
  body?: string;
  createdAt: string; // ISO
  href?: string;     // route to navigate when tapped
  imageUrl?: string | null;
}

const LS_KEY = (uid: string) => `fitaura.notifications.lastSeen.${uid}`;
const LS_CLEARED_KEY = (uid: string) => `fitaura.notifications.clearedAt.${uid}`;
const MAX_ITEMS = 20;

interface BroadcastRow {
  id: string;
  title: string;
  body: string;
  starts_at: string;
}

interface TryonRow {
  id: string;
  kind: string;
  status: string;
  reason: string | null;
  generated_image_url: string | null;
  updated_at: string;
}

interface CreditRow {
  id: string;
  delta: number;
  reason: string;
  created_at: string;
}

interface FeaturedRow {
  id: string;
  status: string;
  reject_reason: string | null;
  reviewed_at: string | null;
  template_label: string | null;
}

const featuredToNotification = (r: FeaturedRow): AppNotification | null => {
  if (!r.reviewed_at) return null;
  if (r.status === "approved") {
    return {
      id: `featured-${r.id}`,
      kind: "featured_approved",
      title: "Your look is featured! ✨",
      body: r.template_label ? `“${r.template_label}” is live on the Featured feed.` : "Tap to see it on the Featured feed.",
      createdAt: r.reviewed_at,
      href: "/app/aura",
    };
  }
  if (r.status === "rejected") {
    return {
      id: `featured-${r.id}`,
      kind: "featured_rejected",
      title: "Featured submission not approved",
      body: r.reject_reason ?? "Try another look — admin couldn't approve this one.",
      createdAt: r.reviewed_at,
      href: "/app/history",
    };
  }
  return null;
};

const tryonToNotification = (r: TryonRow): AppNotification | null => {
  const isAura = r.kind === "aura";
  if (r.status === "complete") {
    return {
      id: `tryon-${r.id}`,
      kind: isAura ? "aura_ready" : "tryon_ready",
      title: isAura ? "Your Aura score is ready" : "Your try-on is ready",
      body: isAura ? "Tap to view the breakdown" : "Tap to see how it looks",
      createdAt: r.updated_at,
      href: isAura ? `/app/aura/view/${r.id}` : `/app/result/${r.id}`,
      imageUrl: r.generated_image_url,
    };
  }
  if (r.status === "failed" || r.status === "refunded") {
    return {
      id: `tryon-${r.id}`,
      kind: "tryon_failed",
      title: isAura ? "Aura analysis failed" : "Try-on failed",
      body: r.reason ?? "Your credit has been refunded.",
      createdAt: r.updated_at,
      href: "/app/history",
      imageUrl: null,
    };
  }
  return null;
};

const creditToNotification = (r: CreditRow): AppNotification | null => {
  if (r.delta <= 0) return null; // only positive deltas surface as notifications
  const isPurchase = r.reason === "purchase";
  return {
    id: `credit-${r.id}`,
    kind: isPurchase ? "credits_purchased" : "credits_granted",
    title: isPurchase
      ? `Purchase complete · +${r.delta} credits`
      : `You earned +${r.delta} credit${r.delta === 1 ? "" : "s"}`,
    body: isPurchase
      ? "Thanks for the support — happy styling!"
      : reasonLabel(r.reason),
    createdAt: r.created_at,
    href: "/app/profile",
  };
};

const broadcastToNotification = (b: BroadcastRow): AppNotification => ({
  id: `broadcast-${b.id}`,
  kind: "broadcast",
  title: b.title,
  body: b.body,
  createdAt: b.starts_at,
  href: "/app/profile",
});

const reasonLabel = (reason: string): string => {
  switch (reason) {
    case "rewarded_ad": return "Rewarded ad bonus";
    case "refund": return "Refunded for a failed generation";
    case "signup_bonus": return "Welcome bonus";
    default: return "Credits added to your account";
  }
};

export const useNotifications = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastSeen, setLastSeen] = useState<string>(() => {
    if (typeof window === "undefined" || !user) return "";
    return window.localStorage.getItem(LS_KEY(user.id)) ?? "";
  });
  const [clearedAt, setClearedAt] = useState<string>(() => {
    if (typeof window === "undefined" || !user) return "";
    return window.localStorage.getItem(LS_CLEARED_KEY(user.id)) ?? "";
  });

  // Refresh stamps when user changes
  useEffect(() => {
    if (!user) { setLastSeen(""); setClearedAt(""); return; }
    setLastSeen(window.localStorage.getItem(LS_KEY(user.id)) ?? "");
    setClearedAt(window.localStorage.getItem(LS_CLEARED_KEY(user.id)) ?? "");
  }, [user]);

  const merge = useCallback((incoming: AppNotification[]) => {
    setItems((prev) => {
      const map = new Map<string, AppNotification>();
      for (const n of [...incoming, ...prev]) map.set(n.id, n);
      return Array.from(map.values())
        .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
        .slice(0, MAX_ITEMS);
    });
  }, []);

  // Initial load
  useEffect(() => {
    if (!user) { setItems([]); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [tryonRes, creditRes, broadcastRes, featuredRes] = await Promise.all([
        supabase
          .from("tryons")
          .select("id,kind,status,reason,generated_image_url,updated_at")
          .eq("user_id", user.id)
          .in("status", ["complete", "failed", "refunded"])
          .order("updated_at", { ascending: false })
          .limit(15),
        supabase
          .from("credit_transactions")
          .select("id,delta,reason,created_at")
          .eq("user_id", user.id)
          .gt("delta", 0)
          .order("created_at", { ascending: false })
          .limit(15),
        supabase
          .from("broadcasts")
          .select("id,title,body,starts_at")
          .order("starts_at", { ascending: false })
          .limit(10),
        supabase
          .from("featured_submissions")
          .select("id,status,reject_reason,reviewed_at,template_label")
          .eq("user_id", user.id)
          .in("status", ["approved", "rejected"])
          .not("reviewed_at", "is", null)
          .order("reviewed_at", { ascending: false })
          .limit(10),
      ]);
      if (cancelled) return;
      const ns: AppNotification[] = [];
      (tryonRes.data ?? []).forEach((r) => {
        const n = tryonToNotification(r as TryonRow);
        if (n) ns.push(n);
      });
      (creditRes.data ?? []).forEach((r) => {
        const n = creditToNotification(r as CreditRow);
        if (n) ns.push(n);
      });
      (broadcastRes.data ?? []).forEach((r) => {
        ns.push(broadcastToNotification(r as BroadcastRow));
      });
      (featuredRes.data ?? []).forEach((r) => {
        const n = featuredToNotification(r as FeaturedRow);
        if (n) ns.push(n);
      });
      merge(ns);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, merge]);

  // Realtime: tryon completions/failures + new credit grants + broadcasts
  useEffect(() => {
    if (!user) return;
    const tag = crypto.randomUUID();
    const ch = supabase.channel(`notifications-${user.id}-${tag}`);

    ch.on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "tryons", filter: `user_id=eq.${user.id}` },
      (payload) => {
        const n = tryonToNotification(payload.new as TryonRow);
        if (n) merge([n]);
      },
    );
    ch.on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "credit_transactions", filter: `user_id=eq.${user.id}` },
      (payload) => {
        const n = creditToNotification(payload.new as CreditRow);
        if (n) merge([n]);
      },
    );
    ch.on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "broadcasts" },
      (payload) => {
        merge([broadcastToNotification(payload.new as BroadcastRow)]);
      },
    );
    ch.on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "featured_submissions", filter: `user_id=eq.${user.id}` },
      (payload) => {
        const n = featuredToNotification(payload.new as FeaturedRow);
        if (n) merge([n]);
      },
    );
    ch.subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user, merge]);

  // Items the user can currently see — anything cleared earlier is hidden.
  const visibleItems = useMemo(() => {
    if (!clearedAt) return items;
    return items.filter((n) => n.createdAt > clearedAt);
  }, [items, clearedAt]);

  const unreadCount = useMemo(() => {
    if (!lastSeen) return visibleItems.length;
    return visibleItems.filter((n) => n.createdAt > lastSeen).length;
  }, [visibleItems, lastSeen]);

  // Opening the popover: just mark seen so the badge clears.
  const markAllRead = useCallback(() => {
    if (!user) return;
    const newest = items[0]?.createdAt ?? "";
    const nowIso = new Date().toISOString();
    const stamp = newest > nowIso ? newest : nowIso;
    try { window.localStorage.setItem(LS_KEY(user.id), stamp); } catch { /* ignore */ }
    setLastSeen(stamp);
  }, [user, items]);

  // Explicit "Clear all": hide every currently visible notification.
  const clearAll = useCallback(() => {
    if (!user) return;
    const newest = items[0]?.createdAt ?? "";
    const nowIso = new Date().toISOString();
    const stamp = newest > nowIso ? newest : nowIso;
    try {
      window.localStorage.setItem(LS_CLEARED_KEY(user.id), stamp);
      window.localStorage.setItem(LS_KEY(user.id), stamp);
    } catch { /* ignore */ }
    setClearedAt(stamp);
    setLastSeen(stamp);
  }, [user, items]);

  return { items: visibleItems, unreadCount, loading, markAllRead, clearAll };
};
